import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./responsive.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useRef, useCallback } from "react";
import Home from "./Pages/Home";
import Listing from "./Pages/Listing";
import ProductDetails from "./Pages/ProductDetails";
import Header from "./Components/Header";
import { createContext, useEffect, useState } from "react";
import axios from "axios";
import Footer from "./Components/Footer";
import ProductModal from "./Components/ProductModal";
import Cart from "./Pages/Cart";
import SignIn from "./Pages/SignIn";
import SignUp from "./Pages/SignUp";
import MyList from "./Pages/MyList";
import Checkout from "./Pages/Checkout";
import Orders from "./Pages/Orders";
import MyAccount from "./Pages/MyAccount";
import SearchPage from "./Pages/Search";
import ChangePassword from "./Pages/ChangePassword";
import ErrorBoundary from "./ErrorHandler";
import PrivacyPolicy from "./Components/Policies/PrivacyPolicy";
import ReturnAndExchangePolicy from "./Components/Policies/ReturnPolicy";
import LegalTerms from "./Components/Policies/TermsNconditions";
import { fetchDataFromApi, postData } from "./utils/api";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useContext } from "react";
import { setAuthenticated, setJwtToken } from "./store/slices/AuthSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "./store/slices/AuthSlice";
import { fetchCartItems } from "./store/slices/CartSlice";
import { fetchWishlistItems } from "./store/slices/WishlistSlice";
import { fetchOrders } from "./store/slices/OrdersSlice";
import store from "./store";

const MyContext = createContext();

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppContent = () => {
  const context = useContext(MyContext);
  const token = useSelector((state) => state.auth.jwtToken);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpenProductModal, setIsOpenProductModal] = useState(false);
  const [isHeaderFooterShow, setIsHeaderFooterShow] = useState(true);
  const [isLogin, setIsLogin] = useState(false);
  const [productData, setProductData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [subCategoryData, setsubCategoryData] = useState([]);
  const [addingInCart, setAddingInCart] = useState(false);
  const [cartData, setCartData] = useState();
  const [searchData, setSearchData] = useState([]);
  const [isOpenNav, setIsOpenNav] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [enableFilterTab, setEnableFilterTab] = useState(false);
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [isBottomShow, setIsBottomShow] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [alertBox, setAlertBox] = useState({
    msg: "",
    error: false,
    open: false,
  });

  const [user, setUser] = useState({
    name: "",
    email: "",
    userId: "",
  });

  const localUser = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/signIn" || location.pathname === "/signUp") {
      setIsHeaderFooterShow(false);
    } else {
      setIsHeaderFooterShow(true);
    }
  }, [location]);

  const dispatchUserData = useCallback(
    async (userId) => {
      try {
        const cartState = store.getState().cart;
        const wishlistState = store.getState().wishlist;
        const ordersState = store.getState().orders;

        const promises = [];

        if (cartState.cartItems.length === 0) {
          promises.push(dispatch(fetchCartItems(userId)));
        }
        if (wishlistState.wishlistItems.length === 0) {
          promises.push(dispatch(fetchWishlistItems(userId)));
        }
        if (ordersState.orders.length === 0) {
          promises.push(dispatch(fetchOrders(userId)));
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } catch (err) {
        toast.error("Failed to sync data");
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        setIsLogin(false);
        setUser({ name: "", email: "", userId: "" });
        localStorage.removeItem("user");
        return;
      }
      setIsLoading(true);
      try {
        const res = await fetchDataFromApi("/api/user/me");
        const userData = res;
        if (userData && userData._id) {
          localStorage.setItem("user", JSON.stringify(userData));
          setUser({
            name: userData.name || "",
            email: userData.email || "",
            userId: userData._id || "",
          });
          setIsLogin(true);
          await dispatchUserData(userData._id);
        } else {
          throw new Error("Invalid user data");
        }
      } catch (error) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        dispatch(logout());
        setUser({ name: "", email: "", userId: "" });
        setIsLogin(false);
        toast.error("Session expired. Please sign in again.");
      } finally {
        setIsLoading(false);
      }
    };
    validateToken();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetchDataFromApi("/api/category");
        setCategoryData(res.categoryList);
        const subCatArr = [];
        res.categoryList?.forEach((cat) => {
          if (Array.isArray(cat?.children)) {
            subCatArr.push(...cat.children);
          }
        });
        setsubCategoryData(subCatArr);
      } catch (error) {
        toast.error("Failed to load categories");
      }
    };

    fetchCategories();
    window.addEventListener("resize", () => setWindowWidth(window.innerWidth));
    return () => window.removeEventListener("resize", () => {});
  }, []);

  const handleClose = (_, reason) => {
    if (reason !== "clickaway") setAlertBox({ open: false });
  };

  const addToCart = async (data) => {
    if (!isLogin)
      return setAlertBox({
        open: true,
        error: true,
        msg: "Please login first",
      });
    try {
      setAddingInCart(true);
      const res = await postData(`/api/cart/add`, data);
      if (res.status !== false) {
        const user = JSON.parse(localStorage.getItem("user"));
        await dispatch(fetchCartItems(user._id));
        setAlertBox({ open: true, error: false, msg: "Item added to cart" });
      } else {
        setAlertBox({ open: true, error: true, msg: res.msg });
      }
    } catch (err) {
      setAlertBox({ open: true, error: true, msg: "Failed to add item" });
    } finally {
      setAddingInCart(false);
    }
  };

  const values = {
    isOpenProductModal,
    setIsOpenProductModal,
    isHeaderFooterShow,
    setIsHeaderFooterShow,
    isLogin,
    setIsLogin,
    user,
    setUser,
    categoryData,
    setCategoryData,
    subCategoryData,
    setsubCategoryData,
    isOpenProductModal,
    alertBox,
    setAlertBox,
    addToCart,
    addingInCart,
    setAddingInCart,
    cartData,
    setCartData,
    searchData,
    setSearchData,
    windowWidth,
    isOpenNav,
    setIsOpenNav,
    setEnableFilterTab,
    enableFilterTab,
    setIsOpenFilters,
    isOpenFilters,
    setIsBottomShow,
    isBottomShow,
    isLoading,
    setIsLoading,
  };

  const LoadingComponent = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </div>
  );

  return (
    <MyContext.Provider value={values}>
      <ScrollToTop />
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <>
          <Snackbar
            open={alertBox.open}
            autoHideDuration={6000}
            onClose={handleClose}
            className="snackbar"
          >
            <Alert
              onClose={handleClose}
              autoHideDuration={6000}
              severity={alertBox.error === false ? "success" : "error"}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {typeof alertBox.msg === "string"
                ? alertBox.msg
                : alertBox.msg?.message || "An error occurred"}
            </Alert>
          </Snackbar>

          {isHeaderFooterShow && <Header />}
          <ToastContainer position="top-center" autoClose={3000} />
          <Routes>
            <Route path="/" exact={true} element={<Home />} />
            <Route
              path="/products/category/:id"
              exact={true}
              element={<Listing />}
            />
            <Route
              path="/products/subCat/:id"
              exact={true}
              element={<Listing />}
            />
            <Route
              exact={true}
              path="/product/:id"
              element={<ProductDetails />}
            />
            <Route exact={true} path="/cart" element={<Cart />} />
            <Route exact={true} path="/signIn" element={<SignIn />} />
            <Route exact={true} path="/signUp" element={<SignUp />} />
            <Route exact={true} path="/my-list" element={<MyList />} />
            <Route exact={true} path="/checkout" element={<Checkout />} />
            <Route exact={true} path="/orders" element={<Orders />} />
            <Route exact={true} path="/my-account" element={<MyAccount />} />
            <Route exact={true} path="/search" element={<SearchPage />} />
            <Route exact={true} path="/privacy" element={<PrivacyPolicy />} />
            <Route
              exact={true}
              path="/returnpolicy"
              element={<ReturnAndExchangePolicy />}
            />
            <Route exact={true} path="/legalterms" element={<LegalTerms />} />
            <Route
              exact={true}
              path="/changePassword"
              element={<ChangePassword />}
            />
          </Routes>

          {isHeaderFooterShow && <Footer />}
          {isOpenProductModal && <ProductModal data={productData} />}
        </>
      )}
    </MyContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
export { MyContext };
